import React, { useEffect, useRef, useState } from 'react';
import { Card, Checkbox, DatePicker, Button, message, Space, Row, Col, Typography, Divider, Popover } from 'antd';
import { CloudUploadOutlined, CalendarOutlined, UserOutlined, DatabaseOutlined, ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ExportProgress from '../shared/ExportProgress';
import downloadService from '../../services/downloadService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const DownloadTables = () => {
  const [selections, setSelections] = useState({
    devis: { main: false, detail: false },
    bonliv: { main: false, detail: false },
    facture: { main: false, detail: false },
    avoir: { main: false, detail: false },
    reference: { clients: false, articles: false }
  });
  const [dateRange, setDateRange] = useState(null);
  const [activeExport, setActiveExport] = useState(null);
  const [progress, setProgress] = useState(null);
  const exporting = useRef(false);
  const mounted = useRef(true);
  const progressPanel = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const busy = activeExport !== null;

  const handleCheckboxChange = (tableType, field) => (e) => {
    setSelections({
      ...selections,
      [tableType]: {
        ...selections[tableType],
        [field]: e.target.checked
      }
    });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // Function to prepare date options
  const getDateOptions = () => {
    const options = {};
    
    // Add date range if selected
    if (dateRange && dateRange[0] && dateRange[1]) {
      options.startDate = dateRange[0];
      options.endDate = dateRange[1];
    }
    
    return options;
  };

  const handleUpload = async (type) => {
    if (exporting.current) return;
    const selection = selections[type];
    if (selection && !selection.main && !selection.detail) return;
    const part = !selection || (selection.main && selection.detail) ? 'both' : selection.main ? 'main' : 'detail';
    exporting.current = true;
    setActiveExport(type);
    setProgress({ stage: 'starting', percent: 0, uploadedCount: 0 });
    progressPanel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    try {
      const result = await downloadService.exportWithProgress({
        type, part, ...getDateOptions(),
        onProgress: event => { if (mounted.current) setProgress(event); }
      });
      if (!mounted.current) return;
      setProgress(result);
      if (result.success) message.success('Export vers le serveur FTP terminé.');
      else message.error(result.message || 'Échec de l’export vers le serveur FTP.');
    } catch (error) {
      if (mounted.current) {
        const errorMessage = error.message || 'Connexion interrompue.';
        setProgress(previous => ({ ...previous, stage: 'failed', message: errorMessage }));
        message.error(errorMessage);
      }
    } finally {
      exporting.current = false;
      if (mounted.current) setActiveExport(null);
    }
  };


  return (
    <Card className="export-tables-card">
      <Row align="middle" justify="space-between" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4}>Exportation des Tables vers le Serveur FTP (Format DBF)</Title>
        </Col>
        <Col>
          <Popover 
            content={
              <div style={{ maxWidth: 400 }}>
                <Text>
                  L'option "Tout Exporter" exportera toutes les tables de transactions (Devis, Bon de Livraison, Factures, Avoirs -
                  entêtes et détails) et toutes les données de référence (Clients, Articles, Stock) vers le serveur FTP.
                  Le filtre de date ne sera appliqué qu'aux données de transactions si sélectionné.
                </Text>
              </div>
            }
            title="Information"
            trigger="hover"
            placement="bottomRight"
          >
            <Button 
              type="primary" 
              icon={<ExportOutlined />} 
              onClick={() => handleUpload('all')}
              loading={activeExport === 'all'}
              disabled={busy}
              size="large"
            >
              Tout Exporter <InfoCircleOutlined style={{ marginLeft: 5 }} />
            </Button>
          </Popover>
        </Col>
      </Row>
      
      <div ref={progressPanel} style={{ scrollMarginTop: 16 }}>
        <ExportProgress progress={progress} title={activeExport === 'all' ? 'Tout exporter' : 'Export FTP'} />
      </div>

      {/* Date picker */}
      <Card 
        title="Période (Optionnel)" 
        size="small" 
        extra={<CalendarOutlined />} 
        style={{ marginBottom: 24 }}
        className="date-filter-card"
      >
        <RangePicker
          disabled={busy}
          onChange={handleDateRangeChange}
          style={{ width: '100%' }}
          placeholder={['Date de début', 'Date de fin']}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
          Si aucune période n'est sélectionnée, toutes les données disponibles seront exportées.
        </Text>
        <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
          Les fichiers seront téléchargés sur le serveur FTP dans le répertoire /celer/tables.
        </Text>
      </Card>
      
      <Divider orientation="left">Tables de Transactions</Divider>
      
      <Row gutter={[16, 16]}>
        {/* Devis Card */}
        <Col xs={24} md={12} xl={6}>
          <Card 
            title="Devis" 
            bordered={true}
            className="transaction-card"
            extra={
              <Button 
                type="primary" 
                icon={<CloudUploadOutlined />} 
                onClick={() => handleUpload('devis')}
                loading={activeExport === 'devis'}
                disabled={busy || (!selections.devis.main && !selections.devis.detail)}
              >
                Exporter
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox 
                disabled={busy}
                onChange={handleCheckboxChange('devis', 'main')}
                checked={selections.devis.main}
              >
                Entête
              </Checkbox>
              <Checkbox 
                disabled={busy}
                onChange={handleCheckboxChange('devis', 'detail')}
                checked={selections.devis.detail}
              >
                Détails
              </Checkbox>
            </Space>
          </Card>
        </Col>
        
        {/* Bon de Livraison Card */}
        <Col xs={24} md={12} xl={6}>
          <Card 
            title="Bon de Livraison" 
            bordered={true}
            className="transaction-card"
            extra={
              <Button 
                type="primary" 
                icon={<CloudUploadOutlined />} 
                onClick={() => handleUpload('bonliv')}
                loading={activeExport === 'bonliv'}
                disabled={busy || (!selections.bonliv.main && !selections.bonliv.detail)}
              >
                Exporter
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox 
                disabled={busy}
                onChange={handleCheckboxChange('bonliv', 'main')}
                checked={selections.bonliv.main}
              >
                Entête
              </Checkbox>
              <Checkbox 
                disabled={busy}
                onChange={handleCheckboxChange('bonliv', 'detail')}
                checked={selections.bonliv.detail}
              >
                Détails
              </Checkbox>
            </Space>
          </Card>
        </Col>
        
        {/* Factures Card */}
        <Col xs={24} md={12} xl={6}>
          <Card 
            title="Factures" 
            bordered={true}
            className="transaction-card"
            extra={
              <Button 
                type="primary" 
                icon={<CloudUploadOutlined />} 
                onClick={() => handleUpload('facture')}
                loading={activeExport === 'facture'}
                disabled={busy || (!selections.facture.main && !selections.facture.detail)}
              >
                Exporter
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox 
                disabled={busy}
                onChange={handleCheckboxChange('facture', 'main')}
                checked={selections.facture.main}
              >
                Entête
              </Checkbox>
              <Checkbox 
                disabled={busy}
                onChange={handleCheckboxChange('facture', 'detail')}
                checked={selections.facture.detail}
              >
                Détails
              </Checkbox>
            </Space>
          </Card>
        </Col>

        {/* Avoirs Card */}
        <Col xs={24} md={12} xl={6}>
          <Card
            title="Avoirs"
            bordered={true}
            className="transaction-card"
            extra={
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={() => handleUpload('avoir')}
                loading={activeExport === 'avoir'}
                disabled={busy || (!selections.avoir.main && !selections.avoir.detail)}
              >
                Exporter
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox
                disabled={busy}
                onChange={handleCheckboxChange('avoir', 'main')}
                checked={selections.avoir.main}
              >
                Entête
              </Checkbox>
              <Checkbox
                disabled={busy}
                onChange={handleCheckboxChange('avoir', 'detail')}
                checked={selections.avoir.detail}
              >
                Détails
              </Checkbox>
            </Space>
          </Card>
        </Col>
      </Row>
      
      <Divider orientation="left">Données de Référence</Divider>
      
      <Row gutter={[16, 16]}>
        {/* Clients Card */}
        <Col xs={24} md={12}>
          <Card 
            title="Clients" 
            bordered={true}
            className="reference-card"
            extra={
              <Button 
                type="primary" 
                icon={<UserOutlined />} 
                onClick={() => handleUpload('clients')}
                loading={activeExport === 'clients'}
                disabled={busy}
              >
                Exporter
              </Button>
            }
          >
            <Text>
              Exporte toutes les données clients au format DBF (CLIENTS.dbf) et les télécharge sur le serveur FTP.
              Les données clients ne sont pas filtrées par date.
            </Text>
          </Card>
        </Col>
        
        {/* Articles Card */}
        <Col xs={24} md={12}>
          <Card 
            title="Articles" 
            bordered={true}
            className="reference-card"
            extra={
              <Button 
                type="primary" 
                icon={<DatabaseOutlined />} 
                onClick={() => handleUpload('articles')}
                loading={activeExport === 'articles'}
                disabled={busy}
              >
                Exporter
              </Button>
            }
          >
            <Text>
              Exporte toutes les données articles au format DBF (ARTICLES.dbf, STOCK.dbf, EMPSTOCK.dbf) 
              et les télécharge sur le serveur FTP. Les données articles ne sont pas filtrées par date.
            </Text>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default DownloadTables; 